import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { EyeOff, Ghost, Mail, MessageSquareOff } from "lucide-react";
import storage from "utils/Storage";
import Cookies from "js-cookie";
import { sendNotfication, API } from 'utils/Helper';
import { useMyProfileQuery, useUpdatePreferencesMutation } from 'hooks/useUser';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background-color: #292d2e;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #3a4142;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  margin: 0;
`;

const Description = styled.p`
  color: #9ca3af;
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #3a4142;
  margin: 20px 0;
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #3a4142;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const PrefInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 80%;
`;

const PrefTitle = styled.div`
  color: #fff;
  font-weight: bold;
  font-size: 14px;
`;

const PrefDesc = styled.div`
  color: #9ca3af;
  font-size: 12px;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #3a4142;
    transition: .4s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background-color: #1fc16b;
  }

  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const Preferences = () => {
  const getUid = () => {
    let uid = Cookies.get('uid');
    if (!uid) {
      try {
        const token = storage.getKey('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          uid = payload.id || payload.uid;
        }
      } catch (e) {
        console.error("Could not parse token", e);
      }
    }
    return uid;
  };

  const uid = getUid();
  const { data: profileData, isLoading } = useMyProfileQuery(uid);
  const updateMutation = useUpdatePreferencesMutation();

  const [prefs, setPrefs] = useState({
    hide_gaming_data: false,
    hide_username: false,
    ghost_mode: false,
    allow_pm: true,
    refuse_friend: false,
    refuse_tip: false,
    promo_emails: true
  });

  useEffect(() => {
    if (profileData?.status && profileData?.data && profileData?.data[0]) {
      const profile = profileData.data[0].profile || {};
      setPrefs({
        hide_gaming_data: !!profile.hide_gaming_data,
        hide_username: !!profile.hide_username,
        ghost_mode: !!profile.ghost_mode,
        allow_pm: profile.allow_pm !== undefined ? !!profile.allow_pm : true,
        refuse_friend: !!profile.refuse_friend,
        refuse_tip: !!profile.refuse_tip,
        promo_emails: profile.promo_emails !== undefined ? !!profile.promo_emails : true
      });
    }
  }, [profileData]);

  const handleToggle = async (key) => {
    if (!uid) return;
    const updatedValue = !prefs[key];
    
    // Optimistic Update
    setPrefs(prev => ({ ...prev, [key]: updatedValue }));

    try {
      await updateMutation.mutateAsync({ uid, [key]: updatedValue });
      sendNotfication("Preference updated successfully", "success");
    } catch (err) {
      sendNotfication("Failed to update preference", "error");
      // Revert state
      setPrefs(prev => ({ ...prev, [key]: !updatedValue }));
    }
  };

  if (isLoading) {
    return <div style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Loading preferences...</div>;
  }

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <EyeOff size={24} color="#f43f5e" />
          <Title>Privacy</Title>
        </CardHeader>
        <Description>
          Manage your account privacy settings.
        </Description>
        <Divider />
        
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Hide my gaming data on profile</PrefTitle>
            <PrefDesc>Even if hidden, your avatar and username are always visible in chatrooms.</PrefDesc>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.hide_gaming_data} 
              onChange={() => handleToggle('hide_gaming_data')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
        
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Hide my username from public lists</PrefTitle>
            <PrefDesc>If hidden, no one can view your profile by clicking on your avatar or username on public rankings or bets list.</PrefDesc>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.hide_username} 
              onChange={() => handleToggle('hide_username')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
      </Card>

      <Card>
        <CardHeader>
          <Ghost size={24} color="#8b5cf6" />
          <Title>Ghost Mode</Title>
        </CardHeader>
        <Description>
          Play anonymously without leaving a trace.
        </Description>
        <Divider />
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Hide my online presence in private chat</PrefTitle>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.ghost_mode} 
              onChange={() => handleToggle('ghost_mode')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
      </Card>

      <Card>
        <CardHeader>
          <MessageSquareOff size={24} color="#0ea5e9" />
          <Title>Chat Preferences</Title>
        </CardHeader>
        <Description>
          Control who can interact with you.
        </Description>
        <Divider />
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Allow private messages from strangers</PrefTitle>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.allow_pm} 
              onChange={() => handleToggle('allow_pm')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Refuse any new friend request</PrefTitle>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.refuse_friend} 
              onChange={() => handleToggle('refuse_friend')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Refuse tip from strangers</PrefTitle>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.refuse_tip} 
              onChange={() => handleToggle('refuse_tip')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
      </Card>
      
      <Card>
        <CardHeader>
          <Mail size={24} color="#10b981" />
          <Title>Marketing</Title>
        </CardHeader>
        <Description>
          Manage your email subscriptions.
        </Description>
        <Divider />
        <PreferenceItem>
          <PrefInfo>
            <PrefTitle>Receive promotional emails</PrefTitle>
            <PrefDesc>Get updates about new bonuses, tournaments, and features.</PrefDesc>
          </PrefInfo>
          <ToggleSwitch>
            <input 
              type="checkbox" 
              checked={prefs.promo_emails} 
              onChange={() => handleToggle('promo_emails')}
            />
            <span />
          </ToggleSwitch>
        </PreferenceItem>
      </Card>
    </PageContainer>
  );
};

export default Preferences;
